// Tap-to-talk audio recorder. Tap to start, pause/resume to append, stop to send or cancel
// to discard. The recording has a MAX_RECORDING_MS time budget that only runs while actually
// recording; when it is used up the recorder pauses itself (it never sends on its own).
// Mirrors the kiosk/test-chat MediaRecorder handling (mime selection, blob assembly,
// <100 byte discard) and exposes a live amplitude for the waveform. Optionally taps the
// mic as 16 kHz LINEAR16 PCM chunks for the live STT stream (see stt-stream.ts).
import { MAX_RECORDING_MS } from './config';

function getSupportedMimeType(): string {
  for (const t of ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}

const STT_SAMPLE_RATE = 16000; // what the backend's streaming recognizer expects

/**
 * Streaming linear-interpolation resampler: Float32 at the AudioContext's native
 * rate (44.1/48 kHz) in, Int16 LINEAR16 at 16 kHz out. The fractional read
 * position carries across chunks so non-integer ratios (44100/16000) stay exact.
 */
class PcmDownsampler {
  private leftover = new Float32Array(0);
  private pos = 0; // fractional read position within `leftover + chunk`

  constructor(private inRate: number) {}

  process(chunk: Float32Array): Int16Array<ArrayBuffer> {
    const ratio = this.inRate / STT_SAMPLE_RATE;
    const buf = new Float32Array(this.leftover.length + chunk.length);
    buf.set(this.leftover);
    buf.set(chunk, this.leftover.length);

    const available = buf.length - 1 - this.pos; // last interpolable position
    const outLen = available >= 0 ? Math.floor(available / ratio) + 1 : 0;
    const out = new Int16Array(outLen);
    let pos = this.pos;
    for (let i = 0; i < outLen; i++) {
      const i0 = Math.floor(pos);
      const t = pos - i0;
      const s0 = buf[i0];
      const s1 = i0 + 1 < buf.length ? buf[i0 + 1] : s0;
      const sample = s0 + (s1 - s0) * t;
      out[i] = Math.max(-32768, Math.min(32767, Math.round(sample * 32767)));
      pos += ratio;
    }
    const keepFrom = Math.min(Math.floor(pos), buf.length);
    this.leftover = buf.slice(keepFrom);
    this.pos = pos - keepFrom;
    return out;
  }
}

export class Recorder {
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private capTimer = 0;
  private cancelled = false;
  private recordedMs = 0; // budget used by finished (paused) segments
  private segmentStart = 0; // performance.now() when the running segment began; 0 = none
  private onLimit: (() => void) | null = null;
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private analyserStream: MediaStream | null = null;
  private buf: Uint8Array<ArrayBuffer> | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private pcmProcessor: ScriptProcessorNode | null = null;
  private pcmSink: GainNode | null = null;

  constructor(private stream: MediaStream) {}

  get recording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }

  get paused(): boolean {
    return this.mediaRecorder?.state === 'paused';
  }

  /** Recording time left in the budget, in ms (paused time does not count). */
  remainingMs(): number {
    const running = this.segmentStart ? performance.now() - this.segmentStart : 0;
    return Math.max(0, MAX_RECORDING_MS - this.recordedMs - running);
  }

  /**
   * Start recording; `onStop` receives the blob, or null if too small to send / errored.
   * When `onPcm` is provided, mic audio is also delivered as raw 16 kHz LINEAR16 PCM
   * chunks (for the live STT stream); best-effort — if the WebAudio tap cannot be set
   * up, recording still works and simply no PCM is delivered. `onLimit` fires when the time
   * budget runs out; the recorder has paused itself by then and waits for stop()/cancel().
   */
  start(
    onStop: (blob: Blob | null) => void,
    onPcm?: (chunk: ArrayBuffer) => void,
    onLimit?: () => void,
  ): void {
    this.chunks = [];
    this.cancelled = false;
    this.recordedMs = 0;
    this.onLimit = onLimit ?? null;
    let finished = false;
    const finishOnce = (blob: Blob | null) => {
      if (finished) return;
      finished = true;
      this.endSegment(); // also covers a recorder stopped from outside (mic track ended)
      this.teardownAnalyser();
      onStop(blob);
    };
    const mime = getSupportedMimeType();
    this.mediaRecorder = new MediaRecorder(this.stream, mime ? { mimeType: mime } : {});
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.mediaRecorder.onstop = () => {
      const type = this.mediaRecorder?.mimeType || 'audio/webm';
      const blob = new Blob(this.chunks, { type });
      this.chunks = [];
      finishOnce(this.cancelled || blob.size < 100 ? null : blob);
    };
    this.mediaRecorder.onerror = () => finishOnce(null);
    this.setupAnalyser();
    if (onPcm) this.setupPcmTap(onPcm);
    // iOS Safari records audio/mp4 and its MediaRecorder is known to deliver empty
    // timeslice chunks (the assembled blob then falls under the 100-byte discard and the
    // recording is silently dropped). We don't stream chunks anywhere, so record mp4 in a
    // single piece and rely on the final dataavailable that stop() flushes before onstop.
    if (mime.startsWith('audio/mp4')) this.mediaRecorder.start();
    else this.mediaRecorder.start(200);
    this.beginSegment();
  }

  /** Pause the recording; the time budget stops running until resume(). */
  pause(): void {
    if (!this.recording) return;
    this.endSegment();
    this.mediaRecorder?.pause();
  }

  /** Append to a paused recording. Returns false when no recording time is left. */
  resume(): boolean {
    if (!this.paused) return this.recording;
    if (this.remainingMs() <= 0) return false;
    this.mediaRecorder?.resume();
    this.beginSegment();
    return true;
  }

  /** Finish the recording (running or paused); `onStop` receives the blob. */
  stop(): void {
    this.endSegment();
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }

  /** Discard the recording; `onStop` receives null. */
  cancel(): void {
    this.cancelled = true;
    this.stop();
  }

  private beginSegment(): void {
    this.segmentStart = performance.now();
    this.capTimer = window.setTimeout(() => {
      if (!this.recording) return;
      this.pause();
      this.recordedMs = MAX_RECORDING_MS; // timer jitter must not leave a sliver of budget
      this.onLimit?.();
    }, this.remainingMs());
  }

  private endSegment(): void {
    if (this.capTimer) {
      clearTimeout(this.capTimer);
      this.capTimer = 0;
    }
    if (this.segmentStart) {
      this.recordedMs += performance.now() - this.segmentStart;
      this.segmentStart = 0;
    }
  }

  /** Current mic peak amplitude in [0,1], for the live waveform. */
  amplitude(): number {
    if (!this.analyser || !this.buf) return 0;
    this.analyser.getByteTimeDomainData(this.buf);
    let peak = 0;
    for (let i = 0; i < this.buf.length; i++) {
      const v = Math.abs(this.buf[i] - 128) / 128;
      if (v > peak) peak = v;
    }
    return Math.min(1, peak * 1.6);
  }

  private setupAnalyser(): void {
    try {
      // Tap a CLONE of the stream for the waveform: on iOS Safari, feeding the same
      // MediaStream to both WebAudio (createMediaStreamSource) and MediaRecorder can
      // silence the recorder's track, producing an empty recording (WebKit quirk).
      this.analyserStream = this.stream.clone();
      this.audioCtx = new AudioContext();
      this.audioCtx.resume().catch(() => {});
      this.sourceNode = this.audioCtx.createMediaStreamSource(this.analyserStream);
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 512;
      this.sourceNode.connect(this.analyser);
      this.buf = new Uint8Array(this.analyser.fftSize);
    } catch {
      this.analyser = null;
      this.sourceNode = null;
    }
  }

  /** PCM tap for the live STT stream, sharing the analyser's cloned stream/context. */
  private setupPcmTap(onPcm: (chunk: ArrayBuffer) => void): void {
    if (!this.audioCtx || !this.sourceNode) return;
    try {
      // ScriptProcessor over AudioWorklet: universally supported (incl. older iOS
      // Safari) and this app has no latency-sensitive audio graph to protect.
      // 4096 frames ≈ 85ms per chunk at 48 kHz — a good streaming cadence.
      const downsampler = new PcmDownsampler(this.audioCtx.sampleRate);
      this.pcmProcessor = this.audioCtx.createScriptProcessor(4096, 1, 1);
      this.pcmProcessor.onaudioprocess = (e) => {
        // Mirror MediaRecorder.pause(): audio heard while paused is not part of the turn.
        if (!this.recording) return;
        const pcm = downsampler.process(e.inputBuffer.getChannelData(0));
        if (pcm.length) onPcm(pcm.buffer);
      };
      this.sourceNode.connect(this.pcmProcessor);
      // A ScriptProcessor only runs while connected to the destination; route it
      // through a muted gain so the mic is never audible.
      this.pcmSink = this.audioCtx.createGain();
      this.pcmSink.gain.value = 0;
      this.pcmProcessor.connect(this.pcmSink);
      this.pcmSink.connect(this.audioCtx.destination);
    } catch {
      this.pcmProcessor = null;
      this.pcmSink = null;
    }
  }

  private teardownAnalyser(): void {
    if (this.pcmProcessor) {
      this.pcmProcessor.onaudioprocess = null;
      try {
        this.pcmProcessor.disconnect();
        this.pcmSink?.disconnect();
      } catch {
        /* ignore */
      }
    }
    try {
      this.audioCtx?.close();
    } catch {
      /* ignore */
    }
    this.analyserStream?.getTracks().forEach((t) => t.stop());
    this.analyserStream = null;
    this.audioCtx = null;
    this.analyser = null;
    this.buf = null;
    this.sourceNode = null;
    this.pcmProcessor = null;
    this.pcmSink = null;
  }
}
